import { FC, FormEvent, useState } from 'react';

import { router, useForm } from '@inertiajs/react';

import { Button } from '../../../../components/ui/Button';
import { csrfTokenHeader } from '../../../../services/CsrfTokenService';
import { CartonProduct, Offer, OfferProperty } from '../../../../types';
import { FormFields } from '../__shared/FormFields';
import {
    OfferDraftFormValues,
    resolveOfferPropertiesForProduct,
} from '../__shared/offerDraft';
import { buildProfileOfferInlineUpdatePath } from '../__shared/returnContext';

interface Props {
    offer: Offer;
    offerProperties: OfferProperty[];
    onCancel: () => void;
    onUpdatedOffer?: (offer: Offer) => void;
}

export const InlineOfferEditor: FC<Props> = ({
    offer,
    offerProperties,
    onCancel,
    onUpdatedOffer,
}) => {
    const [selectedProduct, setSelectedProduct] =
        useState<CartonProduct | null>(offer.product);
    const [processing, setProcessing] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const properties = resolveOfferPropertiesForProduct(
        selectedProduct,
        offerProperties,
    );
    const { data, setData, errors, setError, clearErrors } =
        useForm<OfferDraftFormValues>({
            product_id: offer.product.id.toString(),
            price: offer.price.toString(),
            amount: offer.amount.toString(),
            property_set: offer.property_set ?? {},
            photos: [],
        });
    const isSubmitDisabled =
        !selectedProduct ||
        data.price.trim() === '' ||
        data.amount.trim() === '';

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        clearErrors();
        setSubmitError(null);
        setProcessing(true);

        try {
            const response = await fetch(
                buildProfileOfferInlineUpdatePath(offer.id),
                {
                    method: 'PATCH',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        ...csrfTokenHeader(),
                    },
                    body: JSON.stringify({
                        offer: {
                            product_id: data.product_id,
                            price: data.price,
                            amount: data.amount,
                            property_set: data.property_set,
                        },
                    }),
                },
            );
            const payload = await response.json();

            if (!response.ok) {
                setError(payload.errors ?? {});
                setProcessing(false);
                return;
            }

            const updatedOffer = payload.offer as Offer | undefined;

            if (!updatedOffer) {
                throw new Error('Missing updated offer');
            }

            onUpdatedOffer?.(updatedOffer);
            router.replace({
                preserveScroll: true,
                preserveState: true,
                props: (props) => {
                    const initialOffers = props.initialOffers as
                        | Offer[]
                        | null
                        | undefined;

                    if (!Array.isArray(initialOffers)) {
                        return props;
                    }

                    return {
                        ...props,
                        initialOffers: initialOffers.map((currentOffer) =>
                            currentOffer.id === updatedOffer.id
                                ? updatedOffer
                                : currentOffer,
                        ),
                    };
                },
            });
            onCancel();
            setProcessing(false);
        } catch {
            setSubmitError('Не вдалося оновити пропозицію.');
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm font-medium">Редагувати пропозицію</p>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onCancel}
                    disabled={processing}
                >
                    Скасувати редагування
                </Button>
            </div>

            <FormFields
                selectedProduct={selectedProduct}
                offerProperties={properties}
                amount={data.amount}
                price={data.price}
                propertySet={data.property_set}
                initialProductOptions={[offer.product]}
                setData={setData}
                setSelectedProduct={(product) => setSelectedProduct(product)}
                errors={errors}
                isEditing
                embedded
            />

            {submitError && (
                <p className="text-sm text-red-600">{submitError}</p>
            )}

            <Button
                type="submit"
                variant="success"
                className="h-9 w-full text-sm"
                disabled={processing || isSubmitDisabled}
            >
                Зберегти пропозицію
            </Button>
        </form>
    );
};
